import { useMemo } from 'react';
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateGraphic,
  EmptyStateTitle,
  Icon,
  InlineStack,
  LabelIcon,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelSection,
  PanelDescription,
  SkeletonShape,
  Tooltip,
} from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { Chart } from '@/components/highcharts';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { useEnvHealthDelayHistory, useWorkspaceHealthSimulation } from '@/features/workspace-health';

// Override the default series colors for this chart. Styled mode reads
// --highcharts-color-{n} CSS variables, so we set them on the container.
const seriesColorStyle = {
  '--highcharts-color-0': themeVars.palette['teal-200'],
  '--highcharts-color-1': themeVars.palette['purple_nova-200'],
} as React.CSSProperties;

function formatDelay(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const totalMinutes = Math.round(seconds / 60);
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
}

export function DelayHistoryChart() {
  const workspaceId = getWorkspaceIdFromCurrentUrl() ?? 0;
  const { simulate } = useWorkspaceHealthSimulation();
  const { data, isPending } = useEnvHealthDelayHistory(workspaceId, simulate);

  const hasData =
    (data?.ingress != null && data.ingress.length > 0) || (data?.egress != null && data.egress.length > 0);

  const chartOptions = useMemo(() => {
    const ingressPoints = data?.ingress ?? [];
    const egressPoints = data?.egress ?? [];

    return {
      chart: { type: 'area' as const, height: 200, spacing: [8, 16, 8, 8] },
      xAxis: {
        type: 'datetime' as const,
        labels: {
          format: '{value:%l%P}',
        },
      },
      yAxis: {
        title: { text: undefined },
        min: 0,
        labels: {
          formatter(this: { value: string | number }) {
            return formatDelay(Number(this.value));
          },
        },
      },
      tooltip: {
        shared: true,
        pointFormatter(this: { series: { name: string }; y?: number }) {
          return `${this.series.name}: <b>${formatDelay(this.y ?? 0)}</b><br/>`;
        },
      },
      legend: { align: 'left' as const, verticalAlign: 'top' as const },
      plotOptions: {
        area: {
          fillOpacity: 0.15,
          lineWidth: 1.5,
          marker: { enabled: false },
        },
      },
      series: [
        {
          name: 'Data processing',
          type: 'area' as const,
          data: ingressPoints.map((p) => [p.t * 1000, p.v < 2 ? 0 : p.v]),
        },
        {
          name: 'Outgoing processing',
          type: 'area' as const,
          data: egressPoints.map((p) => [p.t * 1000, p.v < 2 ? 0 : p.v]),
        },
      ],
    };
  }, [data]);

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>Processing delay (last 24 hours)</PanelTitle>
        <PanelDescription as="div">
          <InlineStack gap="050" alignBlock="center">
            Estimated delay at 5-minute intervals.
            <Tooltip content="Calculated based on the expected processing rate for this workspace.">
              <Icon name="help" size="xs" color="subtle" />
            </Tooltip>
          </InlineStack>
        </PanelDescription>
      </PanelHeader>
      <PanelSection>
        {isPending ? (
          <SkeletonShape style={{ width: '100%', height: 200 }} />
        ) : hasData ? (
          <Chart options={chartOptions} containerProps={{ style: seriesColorStyle }} />
        ) : (
          <EmptyState>
            <EmptyStateGraphic>
              <LabelIcon name="analysis" color="grey" size="lg" />
            </EmptyStateGraphic>
            <EmptyStateTitle>No delay data available</EmptyStateTitle>
            <EmptyStateDescription>
              Delay history will appear here once there is enough traffic to measure processing times.
            </EmptyStateDescription>
          </EmptyState>
        )}
      </PanelSection>
    </Panel>
  );
}
